import React from 'react';
import {
  Menu,
  menuClasses,
  MenuItem,
  MenuItemStyles,
  Sidebar,
} from 'react-pro-sidebar';
import { Chip, IconButton, Stack, Typography } from '@mui/material';
import SecurityRoundedIcon from '@mui/icons-material/SecurityRounded';
import Divider from '@mui/material/Divider';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import NotificationIcon from '../../../assets/icons/notification-48.png';
import SimpleMenuItem from '../SubMenu/SimpleMenuItem';
import Groups2RoundedIcon from '@mui/icons-material/Groups2Rounded';
import {
  AirportShuttleRounded,
  Celebration,
  Check,
  ContentPasteSearchRounded,
  EventAvailableRounded,
  MenuBookRounded, ScheduleSend, SellRounded, SnoozeRounded,
} from '@mui/icons-material';
import SportsBaseballRoundedIcon from '@mui/icons-material/SportsBaseballRounded';
import HandshakeRoundedIcon from '@mui/icons-material/HandshakeRounded';
import ApartmentRoundedIcon from '@mui/icons-material/ApartmentRounded';
import EmojiEmotionsRoundedIcon from '@mui/icons-material/EmojiEmotionsRounded';
import EventStaffSubMenu from '../SubMenu/EventStaffSubMenu';
import ManagementSubMenu from '../SubMenu/ManagementSubMenu';
import Version from '../Version';
import { styled, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { UserAvatar } from '../../General/User/UserAvatar';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import {useStateContext} from "../../../hooks/useStateContext";
import {hasPermissionByPriority} from "../../../core/permissions/hasPermission";
import {ROLE_PRIORITIES} from "../../../core/permissions/permissions";
import ReservationCheckSubMenu from "../SubMenu/ReservationCheckSubMenu";

interface SidebarProps {
  collapsed: boolean;
  toggled: boolean;
  setToggled: (value: boolean) => void;
  setBroken: (value: boolean) => void;
  user: any;
  userData: any;
  newNotificationsCount: number;
  shake: boolean;
  handleNotifyOpen: (event: any) => void;
  handleAvatarClick: (event: any) => void;
  toggleMobileMenu: (event: any) => void;
  location: any;
  navigate: (path: string) => void;
  sideBarBgColor: string;
  hasImage: boolean;
  isAdmin?: boolean;
}

const hexToRgba = (hex: string, alpha: number) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);

  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const themes = {
  light: {
    sidebar: {
      backgroundColor: '#ffffff',
      color: '#607489',
    },
    menu: {
      menuContent: '#fbfcfd',
      icon: '#0098e5',
      hover: {
        backgroundColor: '#c5e4ff',
        color: '#44596e',
      },
      disabled: {
        color: '#9fb6cf',
      },
    },
  },
  dark: {
    sidebar: {
      backgroundColor: '#0b2948',
      color: '#8ba1b7',
    },
    menu: {
      menuContent: '#082440',
      icon: '#59d0ff',
      hover: {
        backgroundColor: '#00458b',
        color: '#b6c8d9',
      },
      disabled: {
        color: '#3e5e7e',
      },
    },
  },
};

export const MenuWrapper: React.FC<SidebarProps> = ({
  collapsed,
  toggled,
  setToggled,
  setBroken,
  user,
  userData,
  newNotificationsCount,
  shake,
  handleNotifyOpen,
  toggleMobileMenu,
  handleAvatarClick,
  location,
  navigate,
  sideBarBgColor,
  hasImage,
}) => {
  const themeQuery = useTheme();
  const isMobile = useMediaQuery(themeQuery.breakpoints.down('lg'));
  const isAdmin = hasPermissionByPriority(user.roles, ROLE_PRIORITIES[1]);
  return (
    <>
      {!isMobile ? (
        <Sidebar
          collapsed={collapsed}
          toggled={toggled}
          onBackdropClick={() => setToggled(false)}
          image="https://user-images.githubusercontent.com/25878302/144499035-2911184c-76d3-4611-86e7-bc4e8ff84ff5.jpg"
          breakPoint="lg"
          backgroundColor={sideBarBgColor}
          rootStyles={{
            color: '#fefefe',
            height: '100%',
          }}
        >
          {/* Menü tartalma */}
          <Stack
            sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}
          >
            {/** Menütartalom - ez mindig megjelenik */}
            {MenuContent({
              user,
              userData,
              newNotificationsCount,
              shake,
              handleNotifyOpen,
              handleAvatarClick,
              location,
              navigate,
              collapsed,
              toggleMobileMenu,
              isAdmin,
            })}
          </Stack>
        </Sidebar>
      ) : (
        // Mobilnézetben CSAK a menü tartalma jelenjen meg, a Sidebar nélkül
        <Stack
          sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}
        >
          {MenuContent({
            user,
            userData,
            newNotificationsCount,
            shake,
            handleNotifyOpen,
            handleAvatarClick,
            location,
            navigate,
            collapsed,
            toggleMobileMenu,
            isAdmin,
          })}
        </Stack>
      )}
    </>
  );
};

/**
 * Menü tartalma külön függvényben, hogy mobilon és asztali nézetben is használható legyen.
 */
const MenuContent = ({
  user,
  userData,
  newNotificationsCount,
  shake,
  handleNotifyOpen,
  toggleMobileMenu,
  handleAvatarClick,
  location,
  navigate,
  collapsed,
  isAdmin,
}: Partial<SidebarProps>) => {

  const StyledBadge = styled(Badge)(({ theme }) => ({
    '& .MuiBadge-badge': {
      backgroundColor: '#44b700',
      color: '#44b700',
      boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
      '&::after': {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        borderRadius: '50%',
        animation: 'ripple 1.2s infinite ease-in-out',
        border: '1px solid currentColor',
        content: '""',
      },
    },
    '@keyframes ripple': {
      '0%': {
        transform: 'scale(.8)',
        opacity: 1,
      },
      '100%': {
        transform: 'scale(2.4)',
        opacity: 0,
      },
    },
  }));

  const menuItemStyles: MenuItemStyles = {
    root: {
      fontSize: '13px',
      fontWeight: 400,
    },
    icon: {
      color: '#fff',
      [`&.${menuClasses.disabled}`]: {
        color: themes['light'].menu.disabled.color,
      },
    },
    SubMenuExpandIcon: {
      color: '#b6b7b9',
    },
    subMenuContent: ({ level }) => ({
      backgroundColor:
        level === 0
          ? hexToRgba('#1b635b', !collapsed ? 0.4 : 0.9)
          : 'transparent',
    }),
    button: {
      [`&.${menuClasses.disabled}`]: {
        color: themes['light'].menu.disabled.color,
      },
      '&:hover': {
        backgroundColor: 'rgba(255,255,255,0.12)',
      },
    },
    label: ({ open }) => ({
      fontWeight: open ? 600 : undefined,
    }),
  };

  const { snoozedTodos } = useStateContext();
  const snoozedCount = snoozedTodos?.length || 0;

  return (
    <>
      <Stack
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        }}
      >
        <Menu menuItemStyles={menuItemStyles}>
          <MenuItem
            rootStyles={{
              paddingTop: '15px',
              paddingBottom: '15px',
            }}
            icon={
              <StyledBadge
                overlap="circular"
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                variant="dot"
              >
                <UserAvatar user={userData} />
              </StyledBadge>
            }
            suffix={
              <IconButton
                onClick={toggleMobileMenu}
                sx={{
                  color: '#fff',
                  mr: '-10px',
                  background: '#ffffff1f',
                }}
                size={'small'}
              >
                <ChevronLeftRoundedIcon />
              </IconButton>
            }
            onClick={(event: any) => {
              if (handleAvatarClick) {
                handleAvatarClick(event);
              }
            }}
          >
            <Stack pl={'5px'}>
              <Typography variant={'body1'}>
                {user?.name ? user?.name : '-'}
              </Typography>

              <Stack alignItems={'flex-start'}>
                {user?.roles.map((role: any) => {
                  return (
                    <>
                      <Chip
                        size={'small'}
                        sx={{
                          color: '#c9c9c9',
                          background: '#c9c9c929',
                          '& .MuiChip-icon': {
                            color: '#c9c9c9',
                          },
                        }}
                        icon={
                          <SecurityRoundedIcon fontSize={'small'} sx={{}} />
                        }
                        label={role?.name}
                      />
                    </>
                  );
                })}
              </Stack>
            </Stack>
          </MenuItem>

          <Divider
            sx={{
              borderColor: 'rgb(255 255 255 / 12%)',
            }}
          />

          <MenuItem
            icon={
              collapsed ? (
                <Badge badgeContent={newNotificationsCount} color="error">
                  <Box
                    sx={{
                      width: '24px',
                      height: '24px',
                      objectFit: 'contain',
                      animation:
                        newNotificationsCount && shake
                          ? 'ring 1s ease'
                          : 'none',
                    }}
                  >
                    <img src={NotificationIcon} alt="" />
                  </Box>
                </Badge>
              ) : (
                <Box
                  sx={{
                    width: '24px',
                    height: '24px',
                    objectFit: 'contain',
                    animation:
                      newNotificationsCount && shake ? 'ring 1s ease' : 'none',
                  }}
                >
                  <img src={NotificationIcon} alt="" />
                </Box>
              )
            }
            onClick={(event: any) => {
              if (handleNotifyOpen) {
                handleNotifyOpen(event);
              }
            }}
            suffix={
              <Badge badgeContent={newNotificationsCount} color="error" />
            }
          >
            Notifications
          </MenuItem>
        </Menu>

        <div style={{ flex: 1, marginBottom: '32px' }}>
          <div
            style={{
              padding: '0 24px',
              marginBottom: '5px',
              marginTop: '10px',
            }}
          >
            <Typography
              variant="body2"
              fontWeight={600}
              style={{
                opacity: collapsed ? 0 : 0.7,
                letterSpacing: '0.5px',
              }}
            >
              General
            </Typography>
          </div>

          <Menu menuItemStyles={menuItemStyles}>
            <SimpleMenuItem
              title="Groups"
              icon={<Groups2RoundedIcon />}
              path="/groups"
            />
            <SimpleMenuItem
              title="Activity Reservations"
              icon={<AirportShuttleRounded />}
              path="/activity-reservations"
            />
            <SimpleMenuItem
              title="Service Reservations"
              icon={<MenuBookRounded />}
              path="/service-reservations"
            />
            <SimpleMenuItem
              title="Activities"
              icon={<SportsBaseballRoundedIcon />}
              path="/activities"
            />
            <SimpleMenuItem
              title="Service Providers"
              icon={<HandshakeRoundedIcon />}
              path="/service-providers"
            />
            <SimpleMenuItem
              title="Accommodations"
              icon={<ApartmentRoundedIcon />}
              path="/accommodations"
            />
            <SimpleMenuItem
              title="Sales Partners"
              icon={<EmojiEmotionsRoundedIcon />}
              path="/sales-partners"
            />

            <EventStaffSubMenu
              location={location}
              collapsed={collapsed || false}
              navigate={navigate}
            />

            <ManagementSubMenu
              location={location}
              collapsed={collapsed || false}
            />

            <SimpleMenuItem
              title="Logs"
              icon={<ContentPasteSearchRounded />}
              path="/admin/logs"
            />
            <SimpleMenuItem
              title="Stag Planner"
              icon={<EventAvailableRounded />}
              path="/stag-planner"
            />
            <SimpleMenuItem
              title="Crazy Groups"
              icon={<Celebration />}
              path="/crazy-groups"
            />
           {/* <SimpleMenuItem
              title="Reservation Check"
              icon={<Check />}
              path="/reservation-check"
            />*/}

            <ReservationCheckSubMenu
              location={location}
              collapsed={collapsed || false}
              navigate={navigate}
            />

            <SimpleMenuItem
              title="Prices"
              icon={<SellRounded />}
              path="/sales-partner-prices"
            />

            {isAdmin && (
              <SimpleMenuItem
                title="Waiting for Approval"
                icon={<ScheduleSend />}
                path="/pending-changes"
              />
            )}

            <MenuItem
              icon={
                <Badge badgeContent={snoozedCount} color="error" invisible={snoozedCount === 0}>
                  <SnoozeRounded />
                </Badge>
              }
              suffix={<Badge badgeContent={snoozedCount} color="error" invisible={snoozedCount === 0} />}
              onClick={() => navigate && navigate('/remainders')}
            >
              Remainders
            </MenuItem>
          </Menu>
        </div>

        <Version collapsed={collapsed || false} />
      </Stack>
    </>
  );
};
